import React, { useState } from 'react';
import {
    Box,
    Grid,
    TextField,
    Paper,
    MenuItem,
    Typography,
    FormControlLabel,
    Switch,
    IconButton,
    Tooltip,
} from '@mui/material';
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { green } from '@mui/material/colors';
import Header from '../../components/Header'; // Replace or remove based on your app

const Persona = () => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        active: false,
        type: 'Custom',
        personaEnabled: false,
    });

    const [rows, setRows] = useState([]);
    const [selectionModel, setSelectionModel] = useState([]);

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const addRow = () => {
        setRows((prev) => [
            ...prev,
            {
                id: prev.length + 1,
                functionId: '',
                typeFG: '',
                description: '',
                startDate: '',
                endDate: '',
                active: false,
            },
        ]);
    };

    const CustomToolbar = () => (
        <GridToolbarContainer>
            <Tooltip title="Add Row">
                <IconButton
                    color="primary"
                    onClick={addRow}
                    sx={{
                        backgroundColor: '#1976d2',
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: '#1565c0',
                        },
                        boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                        borderRadius: '8px',
                        width: 40,
                        height: 40,
                    }}
                >
                    <AddIcon />
                </IconButton>
            </Tooltip>
        </GridToolbarContainer>
    );

    const handleEditCellChangeCommitted = ({ id, field, value }) => {
        const updatedRows = rows.map((row) =>
            row.id === id ? { ...row, [field]: value } : row
        );
        setRows(updatedRows);
    };

    const deleteRow = () => {
        const selectedRows = selectionModel; // Assuming `selectionModel` holds the selected row IDs
        const updatedRows = rows.filter((row) => !selectedRows.includes(row.id));
        setRows(updatedRows);
      };

    const columns = [
        { field: 'id', headerName: 'S No', width: 70 },
        {
            field: 'fucntions',
            headerName: 'Roles/Funcitons',
            width: 200,
            editable: true,
        },
        {
            field: 'typeFG',
            headerName: 'Type F/G',
            width: 150,
            editable: true,
        },
        {
            field: 'description',
            headerName: 'Description',
            width: 250,
            editable: true,
        },
        {
            field: 'startDate',
            headerName: 'Start Date',
            width: 150,
            editable: true,
            renderEditCell: (params) => (
                <TextField
                    type="date"
                    value={params.value || ''}
                    onChange={(e) => {
                        const updatedRows = rows.map((row) =>
                            row.id === params.id ? { ...row, startDate: e.target.value } : row
                        );
                        setRows(updatedRows);
                    }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                />
            ),
            renderCell: (params) => <span>{params.value || '-'}</span>,
        },
        {
            field: 'endDate',
            headerName: 'End Date',
            width: 150,
            editable: true,
            renderEditCell: (params) => (
                <TextField
                    type="date"
                    value={params.value || ''}
                    onChange={(e) => {
                        const updatedRows = rows.map((row) =>
                            row.id === params.id ? { ...row, endDate: e.target.value } : row
                        );
                        setRows(updatedRows);
                    }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                />
            ),
            renderCell: (params) => <span>{params.value || '-'}</span>,
        },
        {
            field: 'active',
            headerName: 'Active',
            width: 100,
            editable: true,
            renderCell: (params) => (
                <Switch
                    checked={params.value || false}
                    onChange={(e) => {
                        const updatedRows = rows.map((row) =>
                            row.id === params.id ? { ...row, active: e.target.checked } : row
                        );
                        setRows(updatedRows);
                    }}
                    sx={{
                        '& .Mui-checked': { color: green[500] },
                        '& .Mui-checked + .MuiSwitch-track': {
                            backgroundColor: green[500],
                        },
                    }}
                />
            ),
        },
    ];

    return (
        <Box p={4}>
            <Header title="Persona" subtitle="" />
            <Paper elevation={3} sx={{ p: 3, backgroundColor: '#fff', borderRadius: 2 }}>
                <Grid container spacing={2}>
                    {/* Row 1 */}
                    <Grid item xs={4}>
                        <TextField
                            label="Name"
                            name="name"
                            fullWidth
                            value={formData.name}
                            onChange={handleFormChange}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            label="Description"
                            name="description"
                            fullWidth
                            value={formData.description}
                            onChange={handleFormChange}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            label="Start Date"
                            name="startDate"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            value={formData.startDate}
                            onChange={handleFormChange}
                        />
                    </Grid>

                    {/* Row 2 */}
                    <Grid item xs={4}>
                        <TextField
                            label="End Date"
                            name="endDate"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            value={formData.endDate}
                            onChange={handleFormChange}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            select
                            label="Type"
                            name="type"
                            fullWidth
                            value={formData.type}
                            onChange={handleFormChange}
                        >
                            <MenuItem value="Custom">Custom</MenuItem>
                            <MenuItem value="Seeded">Seeded</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={4}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Typography>Status</Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.active}
                                        name="active"
                                        onChange={handleFormChange}
                                        sx={{
                                            '& .Mui-checked': { color: green[500] },
                                            '& .Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: green[500],
                                            },
                                        }}
                                    />
                                }
                            />
                        </Box>
                    </Grid>

                    {/* Row 3 */}
                    <Grid item xs={4}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Typography>Persona</Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.personaEnabled}
                                        name="personaEnabled"
                                        onChange={handleFormChange}
                                        sx={{
                                            '& .Mui-checked': { color: green[500] },
                                            '& .Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: green[500],
                                            },
                                        }}
                                    />
                                }
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* DataGrid Section */}
            {/* <Box p={4}> */}
            <div>
                {/* Toolbar Section Above the Grid */}
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="100%"
                    sx={{
                        backgroundColor: '#81a9eb',
                        padding: '10px 0', // Vertical padding for spacing
                        borderRadius: 2,  // Optional: Rounded corners for the toolbar
                    }}
                    margin='20px 0'
                >
                    <Tooltip title="Add Row">
                        <IconButton
                            color="primary"
                            onClick={addRow}
                            sx={{
                                backgroundColor: '#5447a7',  // Orange background
                                color: '#fff',               // White icon color
                                '&:hover': {
                                    backgroundColor: '#f57c00',  // Darker orange on hover
                                    transform: 'scale(1.1)',      // Slight zoom effect on hover
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)', // More prominent shadow on hover
                                },
                                boxShadow: '0 3px 5px rgba(0, 0, 0, 0.2)', // Default shadow
                                borderRadius: '50%',         // Make the icon button circular
                                width: 20,                   // Adjusted icon button size
                                height: 20,
                                position: 'relative',        // Make the button absolutely positioned
                                // top: '10px',                 // Position from the top of the container
                                right: '-450px',
                            }}
                        >
                            <AddIcon sx={{ fontSize: 20 }} />  {/* Adjusting the size of the icon */}
                        </IconButton>
                    </Tooltip>

                    {/* Delete Row Icon */}
                    <Tooltip title="Delete Row">
                        <IconButton
                            color="secondary"
                            onClick={deleteRow}  // You should define the deleteRow function
                            sx={{
                                backgroundColor: '#5447a7',  // Orange background
                                color: '#fff',               // White icon color
                                '&:hover': {
                                    backgroundColor: '#f57c00',  // Darker orange on hover
                                    transform: 'scale(1.1)',      // Slight zoom effect on hover
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)', // More prominent shadow on hover
                                },
                                boxShadow: '0 3px 5px rgba(0, 0, 0, 0.2)', // Default shadow
                                borderRadius: '50%',         // Circular button
                                width: 20,                   // Adjusted icon button size
                                height: 20,   
                                position: 'relative',        // Make the button absolutely positioned
                                // top: '10px',                 // Position from the top of the container
                                right: '-500px',               // Adjusted icon button size
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: 20 }} />  {/* Icon size */}
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* DataGrid Section */}
                <div style={{ height: 400, width: '100%' }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        pageSize={5}
                        rowsPerPageOptions={[5]}
                        checkboxSelection
                        disableRowSelectionOnClick
                        onRowSelectionModelChange={(newSelection) =>
                            setSelectionModel(newSelection)
                        }
                        selectionModel={selectionModel}
                        onCellEditCommit={handleEditCellChangeCommitted}
                    />
                </div>
                </div>
            {/* </Box> */}
        </Box>
    );
};

export default Persona;